import React, { useState, useEffect, useMemo } from 'react';
import {
  buildTrackDueDecksFromSnippetRows
} from '../../services/reviewQueueService';
import { ReactComponent as BookIcon } from '../../Icons/book.svg';
import { ReactComponent as CalendarIcon } from '../../Icons/calendar.svg';
import { ReactComponent as HomeInProgressSnippetIcon } from '../../Icons/deck-icon.svg';
import IconRenderer, { normalizeLanguageIconKey } from '../ui/IconRenderer';
import '../Page/Home.css';
import './SRSDashboard.css';

const SRS_DASHBOARD_LANG_ICON_ROW_STYLE = { width: '35px', height: '35px' };

const srsDashboardDeckLanguageKey = (deck) => {
  const raw = String(deck?.language ?? '').trim();
  return normalizeLanguageIconKey(raw) || 'code';
};

/**
 * SRSDashboard — single review queue: library cookbooks and Learn track snippets, due first.
 * Cookbook-only: snippets live within cookbooks.
 *
 * @param {Object} props - Component props
 * @param {Array} props.dueDecks - Cookbooks (non-roadmap) with cards due
 * @param {Array} props.upcomingReviews - Upcoming non-roadmap card reviews in the next few days
 * @param {Array} [props.trackSnippetRows] - Roadmap track cards with SRS history (grouped by cookbook in UI)
 * @param {Array} [props.trackUpcomingReviews] - Upcoming roadmap card reviews
 * @param {boolean} props.loadingSrs - Whether SRS data is currently loading
 * @param {string|null} [props.loadError] - Load failure message
 * @param {Function} [props.onRetryLoad] - Retry after load failure
 * @param {Function} props.handleStudyDeck - (deckId, options?) => start study; options: { startCardId?, dueOnly? }
 * @returns {JSX.Element} The rendered SRS dashboard
 */
const truncateTitle = (title, maxLength = 65) => {
  if (!title) return '';
  if (title.length <= maxLength) return title;
  return title.substring(0, maxLength).trim() + '...';
};

const SRS_DASHBOARD_LIST_PAGE_SIZE = 6;

const SrsDashboardLoadingSkeleton = () => (
  <div
    className="srs-dashboard__layout srs-dashboard__loading-skeleton-shell srs-dashboard__loading-skeleton-shell--pulse"
    role="status"
    aria-busy="true"
    aria-label="Loading spaced repetition data"
  >
    <div className="srs-dashboard__due-shelf">
      <div className="dashboard-section">
        <div className="dash-header srs-dashboard__section-header">
          <div className="srs-dashboard__loading-skeleton-heading" aria-hidden="true">
            <span className="srs-dashboard__loading-skeleton-heading-icon" />
            <span className="srs-dashboard__loading-skeleton-heading-title" />
          </div>
        </div>

        <div className="srs-dashboard__due-queue-filter srs-dashboard__loading-skeleton-filter" aria-hidden="true">
          <span className="srs-dashboard__loading-skeleton-filter-pill" />
          <span className="srs-dashboard__loading-skeleton-filter-pill" />
        </div>

        <div className="srs-dashboard__cookbook-stack home-recent-cookbooks__wrap">
          {[1, 2].map((key) => (
            <div
              key={key}
              className="deck-item home-in-progress__card home-in-progress__card--roadmap-row home-in-progress__card--cookbook srs-dashboard__loading-skeleton-card"
              aria-hidden="true"
            >
              <div className="home-in-progress__roadmap-row-main">
                <div className="home-in-progress__row-leading">
                  <div className="home-in-progress__skeleton-icon home-in-progress__skeleton-icon--row" />
                  <div className="home-in-progress__skeleton-title home-in-progress__skeleton-title--row" />
                </div>
                <div className="home-in-progress__skeleton-stats home-in-progress__skeleton-stats--row" />
              </div>
              <div className="home-in-progress__cookbook-tags home-in-progress__cookbook-tags--skeleton">
                <span className="home-in-progress__skeleton-pill" />
                <span className="home-in-progress__skeleton-pill home-in-progress__skeleton-pill--short" />
              </div>
            </div>
          ))}
        </div>

        <div className="srs-dashboard__loading-skeleton-cta" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const SrsDashboardDueQueueFilter = ({ value, onChange, libraryCount, trackCount }) => {
  const tabs = [
    { id: 'all', label: 'All', count: libraryCount + trackCount },
    { id: 'library', label: 'Cookbooks', count: libraryCount },
    { id: 'track', label: 'Tracks', count: trackCount }
  ];
  return (
    <div
      className="srs-dashboard__due-queue-filter"
      role="tablist"
      aria-label="Filter due items by source"
    >
      {tabs.map((tab) => {
        const selected = value === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            className={[
              'srs-dashboard__due-queue-filter-tab',
              selected ? 'srs-dashboard__due-queue-filter-tab--active' : ''
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onChange(tab.id)}
          >
            <span className="srs-dashboard__due-queue-filter-tab-label">{tab.label}</span>
            <span className="srs-dashboard__due-queue-filter-tab-count" aria-hidden>
              {tab.count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const SrsDashboardListPagination = ({
  pageIndex,
  totalItems,
  pageSize,
  onPrev,
  onNext,
  ariaLabel
}) => {
  if (totalItems <= pageSize) return null;
  const totalPages = Math.ceil(totalItems / pageSize);
  const from = pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, totalItems);
  return (
    <nav className="srs-dashboard__list-pagination" aria-label={ariaLabel}>
      <p className="srs-dashboard__list-pagination-summary">
        Showing {from}–{to} of {totalItems}
        <span className="srs-dashboard__list-pagination-page">
          {' '}
          · Page {pageIndex + 1} of {totalPages}
        </span>
      </p>
      <div className="srs-dashboard__list-pagination-actions">
        <button
          type="button"
          className="srs-dashboard__list-pagination-button"
          onClick={onPrev}
          disabled={pageIndex <= 0}
        >
          Previous
        </button>
        <button
          type="button"
          className="srs-dashboard__list-pagination-button"
          onClick={onNext}
          disabled={pageIndex >= totalPages - 1}
        >
          Next
        </button>
      </div>
    </nav>
  );
};

const SRSDashboard = ({
  dueDecks,
  upcomingReviews,
  trackSnippetRows = [],
  trackUpcomingReviews = [],
  loadingSrs,
  loadError = null,
  onRetryLoad,
  handleStudyDeck
}) => {
  const [duePageIndex, setDuePageIndex] = useState(0);
  const [dueQueueFilter, setDueQueueFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState(null);

  const DashboardSection = ({ title, icon: Icon, iconClassName = '', children, className = '' }) => (
    <div className={`dashboard-section ${className}`}>
      <div className="dash-header srs-dashboard__section-header">
        <h3>
          {Icon && (
            <Icon
              className={['srs-dashboard__section-icon', iconClassName].filter(Boolean).join(' ')}
              aria-hidden
            />
          )}
          {title}
        </h3>
      </div>
      {children}
    </div>
  );

  const trackDueDecks = useMemo(() => {
    const now = new Date();
    // Rows are premium-filtered upstream in Home; pass true so grouping stays consistent.
    return buildTrackDueDecksFromSnippetRows(trackSnippetRows, true, now);
  }, [trackSnippetRows]);

  const trackTotalWithHistory = trackSnippetRows.length;

  const unifiedDueItems = useMemo(() => {
    const library = dueDecks.map((d) => ({
      kind: 'library',
      key: `lib-${d.id}`,
      deck: d
    }));
    const track = trackDueDecks.map((d) => ({
      kind: 'track',
      key: `tr-${d.id}`,
      deck: d
    }));
    return [...library, ...track];
  }, [dueDecks, trackDueDecks]);

  const languageScopedDueItems = useMemo(() => {
    if (!languageFilter) return unifiedDueItems;
    return unifiedDueItems.filter(
      (item) => srsDashboardDeckLanguageKey(item.deck) === languageFilter
    );
  }, [unifiedDueItems, languageFilter]);

  const libraryDueCount = languageScopedDueItems.filter((item) => item.kind === 'library').length;
  const trackDueCookbookCount = languageScopedDueItems.filter((item) => item.kind === 'track').length;

  const filteredDueItems = useMemo(() => {
    if (dueQueueFilter === 'library') {
      return languageScopedDueItems.filter((item) => item.kind === 'library');
    }
    if (dueQueueFilter === 'track') {
      return languageScopedDueItems.filter((item) => item.kind === 'track');
    }
    return languageScopedDueItems;
  }, [languageScopedDueItems, dueQueueFilter]);

  const handleLanguageFilterToggle = (event, languageKey) => {
    event.stopPropagation();
    setLanguageFilter((current) => (current === languageKey ? null : languageKey));
  };
  const visibleDueCount = filteredDueItems.length;

  useEffect(() => {
    setDuePageIndex(0);
  }, [dueQueueFilter, languageFilter]);
  useEffect(() => {
    if (visibleDueCount === 0) return;
    const maxIdx = Math.max(0, Math.ceil(visibleDueCount / SRS_DASHBOARD_LIST_PAGE_SIZE) - 1);
    setDuePageIndex((i) => Math.min(i, maxIdx));
  }, [visibleDueCount]);

  const pagedDueItems = useMemo(() => {
    const start = duePageIndex * SRS_DASHBOARD_LIST_PAGE_SIZE;
    return filteredDueItems.slice(start, start + SRS_DASHBOARD_LIST_PAGE_SIZE);
  }, [filteredDueItems, duePageIndex]);

  const mergedUpcoming = useMemo(() => {
    const a = (upcomingReviews || []).map((u) => ({ ...u, _scope: 'library' }));
    const b = (trackUpcomingReviews || []).map((u) => ({ ...u, _scope: 'track' }));
    return [...a, ...b]
      .filter((u) => u.nextReview)
      .sort((x, y) => new Date(x.nextReview) - new Date(y.nextReview))
      .slice(0, 14);
  }, [upcomingReviews, trackUpcomingReviews]);

  const firstDueDeckId = filteredDueItems[0]?.deck?.id ?? null;

  const onStartNextReview = () => {
    if (firstDueDeckId) handleStudyDeck(firstDueDeckId);
  };

  return (
    <div className="srs-dashboard">
      {loadError ? (
        <div className="srs-dashboard__load-error" role="alert">
          <p className="srs-dashboard__load-error-text">{loadError}</p>
          {typeof onRetryLoad === 'function' ? (
            <button
              type="button"
              className="srs-dashboard__load-error-retry"
              onClick={onRetryLoad}
            >
              Retry
            </button>
          ) : null}
        </div>
      ) : null}

      {loadingSrs ? (
        <SrsDashboardLoadingSkeleton />
      ) : loadError ? null : (
        <div className="srs-dashboard__layout">
          <div className="srs-dashboard__due-shelf">
            <DashboardSection title="Due for review" icon={BookIcon}>
              {unifiedDueItems.length > 0 ? (
                <>
                  <SrsDashboardDueQueueFilter
                    value={dueQueueFilter}
                    onChange={setDueQueueFilter}
                    libraryCount={libraryDueCount}
                    trackCount={trackDueCookbookCount}
                  />
                  {languageFilter ? (
                    <p className="srs-dashboard__language-filter-active" aria-live="polite">
                      Showing due items for{' '}
                      <span className="srs-dashboard__language-filter-active-label">
                        {languageFilter}
                      </span>
                      {' — '}
                      <button
                        type="button"
                        className="srs-dashboard__language-filter-clear"
                        onClick={() => setLanguageFilter(null)}
                      >
                        Clear
                      </button>
                    </p>
                  ) : null}
                  {visibleDueCount > 0 ? (
                    <div className="srs-dashboard__cookbook-stack home-recent-cookbooks__wrap">
                      {pagedDueItems.map((item) => {
                        const deckLanguageKey = srsDashboardDeckLanguageKey(item.deck);
                        const isLanguageFilterActive = languageFilter === deckLanguageKey;
                        const deckLanguageLabel =
                          String(item.deck?.language ?? '').trim() || deckLanguageKey;

                        return (
                          <div
                            key={item.key}
                            className={[
                              'deck-item recent-deck home-in-progress__card home-in-progress__card--roadmap-row home-in-progress__card--cookbook srs-dashboard__cookbook-item',
                              item.kind === 'track' ? 'srs-dashboard__track-cookbook-item' : ''
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={(event) => {
                              if (event.target.closest('.srs-dashboard__language-filter-btn')) return;
                              handleStudyDeck(item.deck.id);
                            }}
                          >
                            <div className="home-in-progress__roadmap-row-main">
                              <div className="home-in-progress__row-leading">
                                <button
                                  type="button"
                                  className="srs-dashboard__language-filter-btn"
                                  onClick={(event) =>
                                    handleLanguageFilterToggle(event, deckLanguageKey)
                                  }
                                  aria-label={
                                    isLanguageFilterActive
                                      ? `Clear ${deckLanguageLabel} filter`
                                      : `Show only ${deckLanguageLabel} due items`
                                  }
                                  aria-pressed={isLanguageFilterActive}
                                  title={
                                    isLanguageFilterActive
                                      ? `Clear ${deckLanguageLabel} filter`
                                      : `Show only ${deckLanguageLabel} due items`
                                  }
                                >
                                  <IconRenderer
                                    name={deckLanguageKey}
                                    type="language"
                                    size="large"
                                    className="home-in-progress__row-language-icon"
                                    style={SRS_DASHBOARD_LANG_ICON_ROW_STYLE}
                                  />
                                </button>
                                <h3 className="home-in-progress__row-title">
                                  {truncateTitle(item.deck.name)}
                                </h3>
                              </div>
                              <div className="home-in-progress__row-trailing">
                                <div
                                  className="home-in-progress__row-stat"
                                  title={`${item.deck.dueCards} card${item.deck.dueCards === 1 ? '' : 's'} due`}
                                  aria-label={`${item.deck.dueCards} card${item.deck.dueCards === 1 ? '' : 's'} due`}
                                >
                                  <span className="home-in-progress__row-stat-icon" aria-hidden>
                                    <HomeInProgressSnippetIcon className="home-in-progress__row-stat-snippet-icon" />
                                  </span>
                                  <span className="home-in-progress__row-stat-value">
                                    {item.deck.dueCards}
                                  </span>
                                </div>
                              </div>
                            </div>
                            {item.deck.tags && item.deck.tags.length > 0 ? (
                              <div className="home-in-progress__cookbook-tags">
                                {item.deck.tags.slice(0, 3).map((tag) => (
                                  <span key={tag} className="home-in-progress__cookbook-tag">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : languageFilter ? (
                    <p className="srs-dashboard__language-filter-empty">
                      No due items for this language with the current source filter.
                    </p>
                  ) : null}
                  {visibleDueCount > 0 ? (
                    <SrsDashboardListPagination
                      pageIndex={duePageIndex}
                      totalItems={visibleDueCount}
                      pageSize={SRS_DASHBOARD_LIST_PAGE_SIZE}
                      onPrev={() => setDuePageIndex((i) => Math.max(0, i - 1))}
                      onNext={() =>
                        setDuePageIndex((i) =>
                          Math.min(
                            Math.max(0, Math.ceil(visibleDueCount / SRS_DASHBOARD_LIST_PAGE_SIZE) - 1),
                            i + 1
                          )
                        )
                      }
                      ariaLabel="Due for review pages"
                    />
                  ) : null}
                </>
              ) : (
                <div className="srs-dashboard__empty-due">
                  <p className="empty-message srs-dashboard__empty-due-line">
                    Nothing due for review right now.
                  </p>
                  {trackTotalWithHistory === 0 ? (
                    <p className="srs-dashboard__empty-hint">
                      <strong>Learn</strong> (roadmap or study deck): submit at least one attempt per
                      card to see track cookbooks and review dates here. Library cookbooks with due cards
                      use the same list.
                    </p>
                  ) : null}
                </div>
              )}

              <button
                type="button"
                className="srs-dashboard__review-all-btn"
                disabled={visibleDueCount === 0}
                onClick={onStartNextReview}
              >
                Start next review
              </button>
            </DashboardSection>
          </div>

          {mergedUpcoming.length > 0 ? (
            <section className="srs-dashboard__upnext" aria-labelledby="srs-dashboard-upnext-title">
              <h4 id="srs-dashboard-upnext-title" className="srs-dashboard__upnext-title">
                <CalendarIcon className="srs-dashboard__upnext-title-icon" aria-hidden />
                Up next
                <span className="srs-dashboard__upnext-sub">next 7 days</span>
              </h4>
              <ul className="srs-dashboard__upcoming-compact">
                {mergedUpcoming.map((snippet) => (
                  <li key={`${snippet.id}-${snippet._scope || ''}`} className="srs-dashboard__upcoming-compact-item">
                    <button
                      type="button"
                      className="srs-dashboard__upcoming-compact-btn"
                      onClick={() =>
                        snippet.deckId &&
                        handleStudyDeck(snippet.deckId, {
                          startCardId: snippet.cardId,
                          dueOnly: false
                        })
                      }
                    >
                      <div className="srs-dashboard__upcoming-compact-main">
                        <span className="srs-dashboard__upcoming-compact-title">{snippet.title}</span>
                        <span className="srs-dashboard__upcoming-compact-when" title="Next review">
                          {new Date(snippet.nextReview).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="srs-dashboard__upcoming-compact-meta">
                        <span className="srs-dashboard__upcoming-scope">
                          {snippet._scope === 'track' ? 'Track' : 'Library'}
                        </span>
                        {snippet.masteryLevel ? (
                          <span className="srs-dashboard__upcoming-mastery-neutral">{snippet.masteryLevel}</span>
                        ) : null}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SRSDashboard;
